// src/components/assignments/AssignModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "components/ui/Modal";
import { Button } from "components/ui/Button";
import { assignItemsToAppUser } from "api/assignments";
import { getItemsForAssign } from "api/items";
import { listAppUsers, getAppUser } from "api/appUsers";
import { User, Package, Plus, Trash2 } from "lucide-react";

export const AssignModal = ({ open, onClose, onCreated, fixedUserId }) => {
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [fixedUserInfo, setFixedUserInfo] = useState(null);

  const [userId, setUserId] = useState("");

  // Состояние текущего выбираемого товара
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");

  // Список товаров к выдаче
  const [addedItems, setAddedItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка при открытии
  useEffect(() => {
    if (!open) return;

    setUserId(fixedUserId ? String(fixedUserId) : "");
    setItemId("");
    setQty(1);
    setPrice("");
    setAddedItems([]); // Очищаем список
    setError(null);
    setFixedUserInfo(null);
    setInitialLoading(true);

    const fetchData = async () => {
      try {
        const itemsPromise = getItemsForAssign();
        let usersPromise;
        if (fixedUserId) {
          usersPromise = getAppUser(fixedUserId);
        } else {
          usersPromise = listAppUsers({ q: "" });
        }

        const [itemsData, usersRes] = await Promise.all([
          itemsPromise,
          usersPromise,
        ]);

        setItems(Array.isArray(itemsData) ? itemsData : []);

        if (fixedUserId) {
          setFixedUserInfo(usersRes || null);
        } else {
          setUsers(Array.isArray(usersRes) ? usersRes : []);
        }
      } catch (e) {
        setError(e.message || "Ma'lumotlarni yuklab bo'lmadi");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [open, fixedUserId]);

  const selectedItem = useMemo(
    () => items.find((it) => String(it.id) === String(itemId)),
    [items, itemId]
  );

  // Автоматически подставляем цену
  useEffect(() => {
    if (selectedItem && selectedItem.price) {
      setPrice(String(selectedItem.price));
    } else if (selectedItem && !selectedItem.price) {
      setPrice("");
    }
  }, [selectedItem]);

  // Расчет суммы текущего товара
  const currentTotal = useMemo(() => {
    const priceNum = price !== "" ? Number(price) : (selectedItem?.price || 0);
    const qtyNum = Number(qty) || 0;
    return priceNum * qtyNum;
  }, [price, qty, selectedItem]);

  // Общая сумма всех добавленных
  const grandTotal = useMemo(() => {
    return addedItems.reduce((acc, it) => acc + (it.price * it.qty), 0);
  }, [addedItems]);

  const clampQty = (n, min = 1, max = undefined) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return min;
    if (num < min) return min;
    if (Number.isFinite(Number(max))) return Math.min(num, Number(max));
    return num;
  };

  // Добавить товар в список
  const handleAddToList = () => {
    if (!itemId || !qty) return;

    const maxAvail = Number(selectedItem?.qty_on_hand ?? 0);
    // Проверяем, сколько уже добавлено такого товара в список
    const alreadyAddedQty = addedItems
      .filter(it => String(it.itemId) === String(itemId))
      .reduce((sum, it) => sum + it.qty, 0);

    const qtyNum = clampQty(qty, 1, maxAvail - alreadyAddedQty);

    if (qtyNum <= 0) {
      setError("Miqdor 0 dan katta bo'lishi kerak");
      return;
    }
    if (qtyNum + alreadyAddedQty > maxAvail) {
      setError(`Omborda faqat ${maxAvail} ta mavjud (ro'yxatda: ${alreadyAddedQty})`);
      return;
    }

    const priceNum = price !== "" ? Number(price) : (selectedItem?.price || 0);

    setAddedItems(prev => [...prev, {
      id: Date.now(), // временный ID для списка
      itemId: Number(itemId),
      itemObj: selectedItem,
      qty: qtyNum,
      price: priceNum,
      total: priceNum * qtyNum
    }]);

    // Сброс полей
    setItemId("");
    setQty(1);
    setPrice("");
    setError(null);
  };

  const handleRemoveFromList = (id) => {
    setAddedItems(prev => prev.filter(it => it.id !== id));
  };

  const submit = async () => {
    if (!userId) {
      setError("Foydalanuvchini tanlang");
      return;
    }
    if (addedItems.length === 0) {
      setError("Hech qanday mahsulot tanlanmagan");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Формируем payload для bulk API
      const itemsPayload = addedItems.map(it => ({
        item_id: it.itemId,
        qty: it.qty,
        price: it.price
      }));

      await assignItemsToAppUser({
        app_user_id: userId,
        items: itemsPayload,
      });

      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Mahsulotlarni berib bo'lmadi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mahsulot berish"
      className="bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-slate-100 max-w-3xl w-full"
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <div className="text-sm">
            <span className="text-gray-500 dark:text-slate-400">Jami summa: </span>
            <span className="font-bold text-lg text-gray-900 dark:text-slate-100">{grandTotal.toLocaleString("ru-RU")} UZS</span>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading} className="text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-800">
              Bekor qilish
            </Button>
            <Button
              onClick={submit}
              disabled={loading || initialLoading || !userId || addedItems.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Berilmoqda..." : `Berish (${addedItems.length})`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Кому */}
        {fixedUserId ? (
          <FormSection title="Kimga" icon={<User className="w-5 h-5" />}>
            {initialLoading ? (
              <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse" />
            ) : fixedUserInfo ? (
              <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-slate-800/50 rounded-lg">
                <Avatar name={fixedUserInfo.full_name} />
                <div className="font-semibold text-gray-800 dark:text-slate-100">
                  {fixedUserInfo.full_name}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-slate-400">
                Foydalanuvchi topilmadi
              </div>
            )}
          </FormSection>
        ) : (
          <FormSection title="Kimga" icon={<User className="w-5 h-5" />}>
            {initialLoading ? (
              <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse" />
            ) : (
              <SearchableSelect
                options={users}
                value={userId}
                onChange={setUserId}
                placeholder="Foydalanuvchini tanlang..."
                renderOption={(u) => (
                  <div className="font-medium text-gray-800 dark:text-slate-200">
                    {u.full_name || `ID ${u.id}`}
                  </div>
                )}
              />
            )}
          </FormSection>
        )}

        {/* Форма добавления */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <h3 className="flex items-center gap-2 text-md font-semibold text-gray-800 dark:text-slate-100 mb-4">
            <Package className="w-5 h-5 text-gray-500 dark:text-slate-400" />
            Mahsulot qo'shish
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Выбор товара (6 cols) */}
            <div className="md:col-span-6">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Mahsulot</label>
              <SearchableSelect
                options={items}
                value={itemId}
                onChange={setItemId}
                placeholder="Tanlang..."
                renderOption={(it) => (
                  <div className="flex justify-between items-center w-full gap-2">
                    <span className="font-medium truncate text-gray-800 dark:text-slate-200 flex-1">
                      {it.name}
                    </span>
                    <span className={`text-xs whitespace-nowrap ${(it.qty_on_hand || 0) > 0 ? "text-gray-500" : "text-red-500"}`}>
                      {it.qty_on_hand} {it.unit}
                    </span>
                  </div>
                )}
              />
            </div>

            {/* Кол-во (3 cols) */}
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Miqdor</label>
              <QuantityInput
                value={qty}
                onChange={(v) => {
                  const max = Number(selectedItem?.qty_on_hand ?? Infinity);
                  setQty(clampQty(v, 1, max));
                }}
                maxValue={selectedItem?.qty_on_hand}
              />
            </div>

            {/* Цена / Кнопка (3 cols) */}
            <div className="md:col-span-3 flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Narx</label>
                <input
                  type="number"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={selectedItem?.price || "0"}
                  className="w-full h-10 px-3 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-slate-100"
                />
              </div>
              <Button
                onClick={handleAddToList}
                disabled={!itemId || !qty}
                className="h-10 px-3 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-lg shrink-0"
                title="Ro'yxatga qo'shish"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Инфо о выбранном */}
          {selectedItem && (
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-slate-400 px-1">
              <span>Mavjud: <b>{selectedItem.qty_on_hand}</b> {selectedItem.unit}</span>
              <span>Summa: <b>{currentTotal.toLocaleString()}</b> UZS</span>
            </div>
          )}
        </div>

        {/* Список добавленных */}
        {addedItems.length > 0 && (
          <div className="border rounded-lg overflow-hidden border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-900/50 text-gray-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Mahsulot</th>
                  <th className="px-4 py-2 text-center font-medium w-20">Miq</th>
                  <th className="px-4 py-2 text-right font-medium w-24">Narx</th>
                  <th className="px-4 py-2 text-right font-medium w-24">Jami</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {addedItems.map((it) => (
                  <tr key={it.id} className="text-gray-900 dark:text-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium">{it.itemObj?.name}</div>
                      <div className="text-xs text-gray-500 dark:text-slate-500">{it.itemObj?.sku || `#${it.itemId}`}</div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      {it.qty} {it.itemObj?.unit}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-slate-400">
                      {Number(it.price).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {Number(it.total).toLocaleString()}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => handleRemoveFromList(it.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error && (
          <p className="text-center text-red-600 text-sm font-medium pt-2">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

/* ===== Локальные UI-хелперы ===== */
const FormSection = ({ title, icon, children }) => (
  <div className="space-y-2">
    <h3 className="flex items-center gap-2 text-md font-semibold text-gray-800 dark:text-slate-100 tracking-tight">
      <span className="text-gray-500 dark:text-slate-400">{icon}</span>
      {title}
    </h3>
    <div>{children}</div>
  </div>
);

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  renderOption,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filtered = query
    ? options.filter((opt) =>
      JSON.stringify(opt).toLowerCase().includes(query.toLowerCase())
    )
    : options;

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)),
    [options, value]
  );

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full h-10 text-left px-3 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm flex items-center justify-between transition-colors hover:border-gray-400 dark:hover:border-slate-500"
      >
        {selected ? (
          <div className="truncate text-gray-900 dark:text-slate-100 flex-1">{renderOption(selected)}</div>
        ) : (
          <span className="text-gray-500 dark:text-slate-400">{placeholder}</span>
        )}
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-slate-700 z-50 p-2 space-y-1 border dark:border-slate-700">
          <input
            type="text"
            placeholder="Qidiruv..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-md placeholder-gray-400 dark:placeholder-slate-500 text-sm"
          />
          <ul className="max-h-60 overflow-y-auto">
            {filtered.map((opt) => (
              <li
                key={opt.id}
                onMouseDown={() => {
                  onChange(String(opt.id));
                  setIsOpen(false);
                  setQuery("");
                }}
                className="px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer text-gray-900 dark:text-slate-100 text-sm"
              >
                {renderOption(opt)}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-gray-500 text-sm text-center">Topilmadi</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const QuantityInput = ({ value, onChange, maxValue }) => {
  const step = (delta) => {
    const max = Number.isFinite(Number(maxValue))
      ? Number(maxValue)
      : undefined;
    const next = Number(value) + delta;
    if (max !== undefined && next > max) return onChange(max);
    if (next < 1) return onChange(1);
    onChange(next);
  };

  return (
    <div className="relative flex items-center h-10">
      <button
        type="button"
        onClick={() => step(-1)}
        className="absolute left-0 h-full px-2 text-gray-600 dark:text-slate-400 hover:text-black dark:hover:text-white bg-gray-50 dark:bg-slate-800 border-r border-gray-300 dark:border-slate-700 rounded-l-lg"
      >
        &minus;
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          const max = Number.isFinite(Number(maxValue))
            ? Number(maxValue)
            : undefined;
          if (!Number.isFinite(v)) return onChange(1);
          if (v < 1) return onChange(1);
          if (max !== undefined && v > max) return onChange(max);
          onChange(v);
        }}
        min="1"
        max={maxValue}
        className="w-full h-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg shadow-sm text-center font-mono text-sm text-gray-900 dark:text-slate-100 px-8"
      />
      <button
        type="button"
        onClick={() => step(1)}
        className="absolute right-0 h-full px-2 text-gray-600 dark:text-slate-400 hover:text-black dark:hover:text-white bg-gray-50 dark:bg-slate-800 border-l border-gray-300 dark:border-slate-700 rounded-r-lg"
      >
        &plus;
      </button>
    </div>
  );
};

const Avatar = ({ name }) => {
  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("") || "?";
  return (
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 font-bold rounded-full text-md">
      {initials}
    </div>
  );
};

export default AssignModal;
